import React from 'react';
import {
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Divider,
  Box,
  RadioGroup,
  Radio,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PhotoLibrary as PhotoLibraryIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ImageLibrarySelectionModal from '../../../accounting/inventory/ImageLibrarySelectionModal';

// Styled components for better layout if needed, or use Box with sx props
const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

interface ReceiptSettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number;
  productGrouping: string;
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  // Logo ayarları (boyutlar frontend'de - local service sadece uygular)
  headerType?: 'text' | 'logo';
  logoUrl?: string;
  logoMaxWidthPx?: number;  // 80mm = 384px. Varsayilan: 384
  logoMaxHeightPx?: number; // Varsayılan: 150 (tüm fiş türleriyle aynı)
}

interface ReceiptSettingsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (settings: ReceiptSettings) => void;
  initialSettings: ReceiptSettings;
  /** Resim kütüphanesi şirket bazlı çalışır; verilmezse logo seçimi kapalıdır. */
  companyId?: string | null;
}

const ReceiptSettingsModal: React.FC<ReceiptSettingsModalProps> = ({
  visible,
  onCancel,
  onSave,
  initialSettings,
  companyId,
}) => {
  const { t } = useTranslation('salesSystem');
  const [formValues, setFormValues] = React.useState<ReceiptSettings>(initialSettings);
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const [imageLibraryOpen, setImageLibraryOpen] = React.useState(false);

  React.useEffect(() => {
    if (visible) {
      setFormValues({
        ...initialSettings,
        showCashierName: typeof initialSettings.showCashierName === 'boolean' ? initialSettings.showCashierName : true,
        headerType: initialSettings.headerType || 'text',
        logoUrl: initialSettings.logoUrl || '',
        logoMaxWidthPx: initialSettings.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: initialSettings.logoMaxHeightPx ?? 150,
      });
      setLogoError(null);
    }
  }, [visible, initialSettings]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const { checked } = event.target as HTMLInputElement;
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormValues(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } 
    else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleNumberChange = (name: keyof ReceiptSettings, value: string | number) => {
    setFormValues(prev => ({ ...prev, [name]: typeof value === 'string' ? parseFloat(value) || 0 : value }));
  };

  // Logo artık yüklenmiyor, resim kütüphanesinden seçiliyor. Kütüphane video da
  // listelediği için seçimi resimle sınırlıyoruz — fiş yazıcısı yalnızca resim basar.
  const handleLogoSelected = (selectedUrls: string[]) => {
    const url = selectedUrls[0];
    setImageLibraryOpen(false);
    if (!url) return;

    if (/\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(url)) {
      setLogoError(t('receiptSettingsModal.errors.imageOnly'));
      return;
    }

    setLogoError(null);
    setFormValues(prev => ({ ...prev, logoUrl: url }));
  };

  const handleLogoDelete = () => {
    setFormValues(prev => ({ ...prev, logoUrl: '' }));
  };

  // ✅ YENİ: Başlık tipi değişikliği
  const handleHeaderTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHeaderType = event.target.value as 'text' | 'logo';
    setFormValues(prev => ({ ...prev, headerType: newHeaderType }));
    
    // Eğer logo seçiliyorsa showReceiptHeader'ı true yap
    if (newHeaderType === 'logo') {
      setFormValues(prev => ({ ...prev, showReceiptHeader: true }));
    }
  };

  const handleSave = () => {
    // Basic validation example, can be expanded
    if (!formValues.companyName) {
      // Potentially show an error message to the user
      console.error("Şirket adı boş olamaz");
      return;
    }
    onSave(formValues);
    onCancel(); // Close modal on save
  };

  return (
    <>
    <Dialog open={visible} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('receiptSettingsModal.title')}</DialogTitle>
      <DialogContent dividers>
        <StyledForm>
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.headerSettings.title')}</Typography>
          
          {/* ✅ YENİ: Başlık Tipi Seçimi */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{t('receiptSettingsModal.headerSettings.selectType')}</Typography>
            <RadioGroup
              row
              value={formValues.headerType || 'text'}
              onChange={handleHeaderTypeChange}
            >
              <FormControlLabel 
                value="text" 
                control={<Radio />} 
                label={t('receiptSettingsModal.headerSettings.textHeader')} 
              />
              <FormControlLabel 
                value="logo" 
                control={<Radio />} 
                label={t('receiptSettingsModal.headerSettings.logoHeader')} 
              />
            </RadioGroup>
          </Box>

          {/* Metin Başlık Bölümü */}
          {formValues.headerType !== 'logo' && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('receiptSettingsModal.companyInfo.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="companyName"
                    label={t('receiptSettingsModal.companyInfo.companyName')}
                    value={formValues.companyName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="taxNumber"
                    label={t('receiptSettingsModal.companyInfo.taxNumber')}
                    value={formValues.taxNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
              <TextField
                name="companyAddress"
                label={t('receiptSettingsModal.companyInfo.address')}
                value={formValues.companyAddress}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {/* ✅ YENİ: Logo Başlık Bölümü */}
          {formValues.headerType === 'logo' && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('receiptSettingsModal.logoSection.selectionTitle')}
              </Typography>
              
              {logoError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {logoError}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {formValues.logoUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Logolar kare olmadığı için önizleme oranı korur (contain);
                        kırpma yapmaz, kutu resmin oranına göre büzülür. */}
                    <Box
                      component="img"
                      src={formValues.logoUrl}
                      alt={t('receiptSettingsModal.logoSection.logoAlt')}
                      sx={{
                        maxWidth: 220,
                        maxHeight: 110,
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        p: 0.5,
                        bgcolor: 'common.white',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" color="success.main" gutterBottom>
                        {t('receiptSettingsModal.logoSection.logoSelected')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PhotoLibraryIcon />}
                          onClick={() => setImageLibraryOpen(true)}
                          disabled={!companyId}
                        >
                          {t('receiptSettingsModal.logoSection.changeLogo')}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={handleLogoDelete}
                        >
                          {t('receiptSettingsModal.logoSection.deleteLogo')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={() => setImageLibraryOpen(true)}
                      disabled={!companyId}
                    >
                      {t('receiptSettingsModal.logoSection.selectLogo')}
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      {t('receiptSettingsModal.logoSection.selectionHint')}
                    </Typography>
                    {!companyId && (
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'warning.main' }}>
                        {t('receiptSettingsModal.logoSection.companyMissing')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="logoMaxWidthPx"
                    label={t('receiptSettingsModal.logoSection.maxWidth')}
                    type="number"
                    value={formValues.logoMaxWidthPx ?? 384}
                    onChange={(e) => handleNumberChange('logoMaxWidthPx', parseInt(e.target.value) || 384)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    helperText={t('receiptSettingsModal.logoSection.maxWidthHint')}
                    inputProps={{ min: 48, max: 576 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="logoMaxHeightPx"
                    label={t('receiptSettingsModal.logoSection.maxHeight')}
                    type="number"
                    value={formValues.logoMaxHeightPx ?? 150}
                    onChange={(e) => handleNumberChange('logoMaxHeightPx', parseInt(e.target.value) || 150)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    helperText={t('receiptSettingsModal.logoSection.maxHeightHint')}
                    inputProps={{ min: 48, max: 300 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="showReceiptHeader"
                checked={formValues.showReceiptHeader}
                onChange={handleChange}
                disabled={formValues.headerType === 'logo'} // Logo seçiliyse otomatik true
              />
            }
            label={t('receiptSettingsModal.headerSettings.showReceiptHeader')}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.printSettings.title')}</Typography>
          <FormControlLabel
            control={<Checkbox name="showDiscountInfo" checked={formValues.showDiscountInfo} onChange={handleChange} />}
            label={t('receiptSettingsModal.printSettings.showDiscountInfo')}
          />
          <FormControlLabel
            control={<Checkbox name="showVat" checked={formValues.showVat} onChange={handleChange} />}
            label={t('receiptSettingsModal.printSettings.showVat')}
          />
          <FormControlLabel
            control={<Checkbox name="showCashierName" checked={formValues.showCashierName} onChange={handleChange} />}
            label={t('receiptSettingsModal.printSettings.showCashierName')}
          />
          
          <FormControlLabel
            control={<Checkbox name="showReceiptNumber" checked={formValues.showReceiptNumber} onChange={handleChange} />}
            label={t('receiptSettingsModal.printSettings.showReceiptNumber')}
          />
          
          {formValues.showReceiptNumber && (
            <Box sx={{ pl: 4, mb: 1 }}>
              <TextField
                name="receiptNumberResetValue"
                label={t('receiptSettingsModal.printSettings.receiptNumberResetValue')}
                type="number"
                value={formValues.receiptNumberResetValue}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 0 && value <= 999999) {
                    handleNumberChange('receiptNumberResetValue', value);
                  }
                }}
                fullWidth
                variant="outlined"
                size="small"
                helperText={t('receiptSettingsModal.printSettings.receiptNumberResetHelp')}
                InputProps={{ 
                  inputProps: { min: 1, max: 999999 }
                }}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    name="resetAtEndOfDay"
                    checked={formValues.resetAtEndOfDay}
                    onChange={handleChange}
                  />
                }
                label={t('receiptSettingsModal.printSettings.resetAtEndOfDay')}
                sx={{ mt: 1 }}
              />
            </Box>
          )}
          
          <Box sx={{ mt: 1, mb: 1 }}>
            <TextField
              name="printCopyCount"
              label={t('receiptSettingsModal.printSettings.printCopyCount')}
              type="number"
              value={formValues.printCopyCount}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1 && value <= 9) {
                  handleNumberChange('printCopyCount', value);
                }
              }}
              fullWidth
              variant="outlined"
              size="small"
              helperText={t('receiptSettingsModal.printSettings.printCopyCountHelp')}
              InputProps={{ 
                inputProps: { min: 1, max: 9 }
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.footerSettings.title')}</Typography>
          <TextField
            name="standardFooterNote"
            label={t('receiptSettingsModal.footerSettings.standardNote')}
            value={formValues.standardFooterNote}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            size="small"
          />
          <TextField
            name="thankYouMessage"
            label={t('receiptSettingsModal.footerSettings.thankYouMessage')}
            value={formValues.thankYouMessage}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            size="small"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>{t('receiptSettingsModal.formatSettings.title')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="receiptWidth"
                label={t('receiptSettingsModal.formatSettings.receiptWidth')}
                type="number"
                value={formValues.receiptWidth}
                onChange={(e) => handleNumberChange('receiptWidth', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="characterLength"
                label={t('receiptSettingsModal.formatSettings.characterLength')}
                type="number"
                value={formValues.characterLength}
                onChange={(e) => handleNumberChange('characterLength', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
          
          <Typography variant="subtitle1" gutterBottom sx={{mt: 2}}>{t('receiptSettingsModal.formatSettings.pageMargins')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginTop"
                label={t('receiptSettingsModal.formatSettings.marginTop')}
                type="number"
                value={formValues.marginTop}
                onChange={(e) => handleNumberChange('marginTop', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginBottom"
                label={t('receiptSettingsModal.formatSettings.marginBottom')}
                type="number"
                value={formValues.marginBottom}
                onChange={(e) => handleNumberChange('marginBottom', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginLeft"
                label={t('receiptSettingsModal.formatSettings.marginLeft')}
                type="number"
                value={formValues.marginLeft}
                onChange={(e) => handleNumberChange('marginLeft', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                name="marginRight"
                label={t('receiptSettingsModal.formatSettings.marginRight')}
                type="number"
                value={formValues.marginRight}
                onChange={(e) => handleNumberChange('marginRight', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </StyledForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('receiptSettingsModal.common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">{t('receiptSettingsModal.common.save')}</Button>
      </DialogActions>
    </Dialog>

    <ImageLibrarySelectionModal
      open={imageLibraryOpen}
      onClose={() => setImageLibraryOpen(false)}
      onSave={handleLogoSelected}
      companyId={companyId ?? null}
      productName={t('receiptSettingsModal.logoSection.logoAlt')}
      currentImageUrls={formValues.logoUrl ? [formValues.logoUrl] : []}
      productId={null}
      singleSelect
    />
    </>
  );
};

export default ReceiptSettingsModal; 